
import { Vue, Component, Ref } from 'vue-property-decorator';
import skeleton from '@/components/skeleton.vue';
import plateKeyboard from '@/components/plate-keyboard.vue';
import helperBox from '@/components/helper-box.vue';
import footer from '@/components/footer.vue';
import loading from '@/components/loading.vue';
import disabledPlateDialog from '@/components/disabled-plate-dialog.vue';
import launchWeapp from '@/components/launch-weapp.vue';
import rrybAdvert from '@/components/rryb-advert.vue';
import {
    Authorize,
    getToken,
    UserCenter,
    PlateQuery,
    Pay,
    DisabledPlate,
    CanIUsePlateKeyboard,
    DisabledAdvertParks,
    WebPayVersion,
    refreshStaticResource,
} from '@/service';

import {
    token,
    tokenClear,
    tokenParse,
    tokenCheck,
    encodeBase64,
    decodeBase64,
    userAgent,
    sign,
    parseExitParkId,
} from '@/assets/ts/util';

@Component({
    name: 'Index',
    components: {
        rrybAdvert,
        skeleton,
        plateKeyboard,
        helperBox,
        loading,
        rrybFooter: footer,
        disabledPlateDialog,
        launchWeapp,
    },
})
export default class Index extends Vue {

    get isLimitPark(): boolean {
        return !!this.$route.query.show_name;
    }

    // 广告是否隐藏（禁投名单命中车场；名单未加载时出口码流量先隐藏，见 store getter）
    get advertHidden(): boolean {
        return this.$store.getters.advertHidden;
    }

    get limitParkName(): string {
        return this.isLimitPark
            ? decodeURIComponent(this.$route.query.show_name as string)
            : '';
    }

    @Ref('keyboard')
    public readonly keyboard!: plateKeyboard;

    @Ref('searchButton')
    public readonly searchButton!: HTMLButtonElement;

    @Ref('header')
    public readonly header!: HTMLDivElement;

    public $message!: any;

    private isSkeleton: boolean = true;
    private plate: string = '';
    private isButtonHidden: boolean = false;
    private userInfo: any = {};
    private loading: boolean = false;
    private isKeyboardDisabled: boolean = false;
    private disabledPlates: string[] = [];
    private canIUsePlateKeyboardWhenGateEmpty: boolean = false;
    private showDisabledPlateDialog: boolean = false;
    private searchPlate: string = '';

    public async search(plate: string, insert: boolean = true) {
        if (plate.length < 6) {
            this.$message('请输入有效车牌');
            return;
        }
        this.searchPlate = plate;
        if (this.disabledPlates.includes(plate)) {
            this.showDisabledPlateDialog = true;
            return;
        }
        if (this.keyboard && insert) {
            this.keyboard.insertHistoryPlate(plate);
        }

        this.loading = true;
        try {
            this.$store.commit('USABLE_COUPON', []);
            this.$store.commit('USABLE_REBATE', []);
            this.$store.commit('PARK_DETAIL', {});
            this.$store.commit('QUERY_RESULT', {});
            this.$store.commit('PICK_COUPON', {});

            const param: any = {
                plate_number: encodeURIComponent(plate),
            };
            if (this.$route.query.park_id) {
                param.park_id = this.$route.query.park_id;
            }
            const { rt_code, data, msg } = await PlateQuery(param);
            const queryPlate = this.$route.query.plate?.toString() ?? '';
            switch (rt_code) {
                case 0:
                    // 出口码需要查询到实际车牌才能判断黑名单
                    if (this.disabledPlates.includes(data.plate)) {
                        this.searchPlate = data.plate;
                        this.showDisabledPlateDialog = true;
                        return;
                    }
                    this.$store.commit('QUERY_RESULT', data);
                    this.$router.push({ name: 'pay' });
                    this.isKeyboardDisabled = false;
                    break;
                default:
                    if (/^rryb/.test(queryPlate)
                        && /查询不到/.test(msg)
                        && this.canIUsePlateKeyboardWhenGateEmpty === false) {
                        this.isKeyboardDisabled = true;
                        return;
                    }
                    this.$message({
                        message: msg,
                        center: true,
                    });
                    break;
            }
        } catch (err) {
            if (err.status !== void 0) {
                if (err.status === 429) {
                    this.$message({
                        message: '查询次数过多，请稍后再试',
                        center: true,
                    });
                    return;
                }
            }
            this.$message({
                message: err.message || err,
                center: true,
            });
            console.warn(err);
        } finally {
            this.loading = false;
            this.isSkeleton = false;
        }
    }

    public searchWrap() {
        if (this.keyboard) {
            this.search(this.keyboard.currentPlate);
        } else {
            console.warn('ref.keyboard is not defined');
        }
    }

    // 检查 search-button 是否被遮挡
    public fixButtonIfHidden() {
        const buttonBottom = this.searchButton.getBoundingClientRect().bottom;
        const keyboardTop = this.keyboard.keyboard.getBoundingClientRect().top;
        this.isButtonHidden = buttonBottom > keyboardTop;
    }

    public async init() {
        const callback = location.href
            + (location.search ? '&' : '?')
            + 'repeat=1';

        if (tokenCheck()) {
            this.userInfo = tokenParse() || {};
            if (this.userInfo.phone
                || this.$route.query.repeat === '1'
                || userAgent() === 'H5'
            ) {
                if (this.$route.query.plate) { // 无牌车
                    this.search(this.$route.query.plate as string, false);
                } else if (userAgent() === 'Wechat'
                    && this.$route.query.pay_id
                ) { // 小程序内嵌接招行一网通支付
                    this.CMBPay();
                } else {
                    try {
                        const { status, data } = await UserCenter.majorPlate();
                        if (status === 1 && data?.length > 0) {
                            this.plate = data[0].platenumber;
                        }
                    } catch (error) {
                        console.warn(error);
                    }
                    this.isSkeleton = false;
                }
            } else {
                tokenClear();
                Authorize(encodeBase64(callback));
            }

        } else if (this.$route.query.token_sign
            && this.$route.query.unionid
        ) { // 用户中心2.0换token
            if (userAgent() === 'Wechat' && !this.$route.query.openid) {
                Authorize(encodeBase64(callback));
            } else {
                const { unionid, token_sign } = this.$route.query;

                try {
                    const tokenData: any = await this.getToken(unionid, token_sign);
                    this.isSkeleton = false;
                    token(tokenData.token);
                    this.init();
                } catch (error) {
                    console.warn(error);
                }
            }
        } else if (this.$route.query.token && this.$route.query.pay_id) {
            this.CMBPay();
        } else if (this.$route.query.is_alimini === '1') {
            alert('支付宝小程序');
            this.isSkeleton = false;
        } else {
            // Authorize(encodeBase64(callback));
            // 新版授权系统不允许直接拉授权，用户可进入简易查车费模式
            if (this.$route.query.plate) { // 无牌车
                this.search(this.$route.query.plate as string, false);
            }
            this.isSkeleton = false;
        }
    }

    public getToken(unionid: any, tokenSign: any): Promise<any> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data } = await getToken(unionid, tokenSign);
                resolve(data);
            } catch (err) {
                console.warn(err);
                reject(err);
            }
        });
    }

    public CMBPay(): void {
        wx.miniProgram.getEnv((res: any) => {
            console.log('wx.miniProgram env', res);
            if (res.miniprogram) {
                Pay.CMBpay({
                    'pay_id': this.$route.query.pay_id,
                    'coupon_ids': this.$route.query.coupon_ids,
                    'src': 'mini',
                    'RRYB-Auth': this.$route.query.token,
                });
            } else {
                this.isSkeleton = false;
            }
        });
    }
    public  openAdd() {
      // window.location=''
      window.open('http://stc.mimilanya.cn/cp/iHwiUw');
  }

    /**
     * 初始化安泊广告 SDK（自 public/index.html 迁入）
     * @desc 原先页面加载即无条件 init（对所有路由页面生效）且不受任何开关控制；
     *       迁入后仅在广告禁投名单决策完成且未命中时初始化，adPosId=3 本就是
     *       首页广告位；SDK script 加载失败（window.__anbo_adv_sdk__ 不存在）时静默跳过
     * @author logic
     * @date 2026-10-09
     */
    public initAnboAdvert(): void {
        const sdk = (window as any).__anbo_adv_sdk__;
        if (!sdk) {
            console.warn('安泊广告 SDK 未加载，跳过初始化');
            return;
        }
        sdk.init({
            appid: 'abU8cO6cYpZO5qZC1b', // 接入key（必填）
            parkId: 'P19531621688PONY6', // 车场ID（必填）
            adPosId: 3, // 广告位置 3:输入车牌页面广告、 4:收银台广告（必填）
            phone: '', // 手机号码 （非必填）
            wxAppid: 'wxb81e3f60edef163c', // 微信公众号appid （必填）
            wxOpenid: 'oQHaHv4i_6SwZqSdadNiYrdRgjmY', // 微信公众号openid （必填）
            carNo: '桂A12345', // 车牌号码 （必填）
            carBrand: 'aodi', // 车辆品牌
        });
    }

    private async created() {
        try {
            // 出口码流量解析车场ID并拉取广告禁投名单（payadmin 下发）。
            // 非阻塞：不进下方 Promise.all，接口失败快速兜底空名单=照常显示广告
            this.$store.commit('ADVERT_EXIT_PARK', parseExitParkId(this.$route.query.plate));
            DisabledAdvertParks().then((parks) => {
                this.$store.commit('DISABLED_ADVERT_PARKS', parks);
                // 名单决策完成后，未命中才初始化安泊广告 SDK（init 自 index.html 迁入）
                if (!this.$store.getters.advertHidden) {
                    this.initAnboAdvert();
                }
            });

            const { data: versionData } = await WebPayVersion();
            const version = +(versionData.option_value ?? 0);
            if (window.SERVER_CONFIG.VERSION < version) {
                refreshStaticResource().then(() => {
                    this.$message('已更新网页支付版本');
                    location.href = location.href;
                }).catch((err: any) => {
                    this.$message('更新网页支付版本出错，请联系客服' + err);
                });
            }
            const [
                { data: disabledPlateData },
                { data: canIUsePlateKeyboard },
            ] = await Promise.all([
                DisabledPlate(),
                CanIUsePlateKeyboard(),
            ]);
            this.disabledPlates = JSON.parse(disabledPlateData?.option_value || '[]');
            this.canIUsePlateKeyboardWhenGateEmpty = +(canIUsePlateKeyboard?.option_value) === 1
                ? true
                : false;
            this.init();
        } catch (error) {
            console.warn(error);
            this.disabledPlates = [];
            this.init();
        }
    }
}
